import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Alert } from 'react-bootstrap';
import termService from '@/shared/services/termService';
import {
    formatDate,
    formDateToStored,
    releaseSentence,
} from '@/components/common/programTermDates';
import { termDisplayName } from '@/shared/utils/termLabel';

/**
 * The dialog where a program's coordinator sets its application, review and
 * decision-release dates and per-faculty application extensions. The form
 * fills from the term each time the dialog opens.
 */
function ProgramDeadlinesDialog({
    show,
    onHide,
    programType,
    programLabel,
    programName,
    selectedTerm,
    onTermUpdated,
}) {
    const [saving, setSaving] = useState(false);
    const [deadlineError, setDeadlineError] = useState(null);
    const [windowForm, setWindowForm] = useState({
        applicationOpenDate: '',
        applicationCloseDate: '',
        reviewStart: '',
        reviewEnd: '',
        decisionsReleased: '',
    });
    const [extForm, setExtForm] = useState({
        facultyEmail: '',
        applicationCloseDate: '',
    });
    const [extError, setExtError] = useState(null);
    const [extSaving, setExtSaving] = useState(false);
    const [editingEmail, setEditingEmail] = useState(null);
    const [extSuccess, setExtSuccess] = useState(null);

    const pw = selectedTerm?.programWindows?.[programType];

    // The application dates as the dialog opened with them, so a save sends
    // only the ones the coordinator changed and cannot quietly undo a date
    // someone else set in the meantime.
    const [loadedApplicationDates, setLoadedApplicationDates] = useState({});
    // Whether the release date had a value when the dialog opened: emptying it
    // then is a clear, which releases every held decision.
    const [loadedDecisionsReleased, setLoadedDecisionsReleased] = useState('');
    const clearingRelease =
        Boolean(loadedDecisionsReleased) && !windowForm.decisionsReleased;

    const toDateInput = (value) =>
        value ? new Date(value).toISOString().split('T')[0] : '';

    const populate = () => {
        setWindowForm({
            // The program's own dates only; blank means it uses the term's.
            applicationOpenDate: toDateInput(pw?.applicationOpenDate),
            applicationCloseDate: toDateInput(pw?.applicationCloseDate),
            reviewStart: pw?.reviewStart
                ? new Date(pw.reviewStart).toISOString().split('T')[0]
                : '',
            reviewEnd: pw?.reviewEnd
                ? new Date(pw.reviewEnd).toISOString().split('T')[0]
                : '',
            decisionsReleased: toDateInput(pw?.decisionsReleased),
        });
        setLoadedDecisionsReleased(toDateInput(pw?.decisionsReleased));
        setLoadedApplicationDates({
            applicationOpenDate: toDateInput(pw?.applicationOpenDate),
            applicationCloseDate: toDateInput(pw?.applicationCloseDate),
        });
        setDeadlineError(null);
        resetExtForm();
    };

    const handleSaveDeadlines = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setDeadlineError(null);
            // An application date is sent only when it changed; an emptied
            // field is sent empty, which clears the program's own date so it
            // falls back to the term's.
            const data = {};
            for (const field of [
                'applicationOpenDate',
                'applicationCloseDate',
            ]) {
                if (windowForm[field] !== loadedApplicationDates[field]) {
                    data[field] = windowForm[field];
                }
            }
            if (windowForm.reviewStart)
                data.reviewStart = windowForm.reviewStart;
            if (windowForm.reviewEnd) data.reviewEnd = windowForm.reviewEnd;
            if (windowForm.decisionsReleased)
                data.decisionsReleased = windowForm.decisionsReleased;
            else if (loadedDecisionsReleased) data.decisionsReleased = '';

            const updatedTerm = await termService.updateProgramWindow(
                selectedTerm.code,
                programType,
                data
            );
            onHide();
            if (onTermUpdated) onTermUpdated(updatedTerm);
        } catch (err) {
            // The server names what is wrong with a date; say that rather
            // than a generic failure.
            setDeadlineError(
                err.response?.data?.message ||
                    'Failed to save deadlines. Please try again.'
            );
        } finally {
            setSaving(false);
        }
    };

    const resetExtForm = () => {
        setExtForm({ facultyEmail: '', applicationCloseDate: '' });
        setEditingEmail(null);
        setExtError(null);
        setExtSuccess(null);
    };

    const handleSaveExtension = async (e) => {
        e.preventDefault();
        try {
            setExtSaving(true);
            setExtError(null);
            setExtSuccess(null);
            const data = {
                facultyEmail: extForm.facultyEmail,
                programType,
                applicationCloseDate: extForm.applicationCloseDate,
            };
            let updatedTerm;
            if (editingEmail === null) {
                updatedTerm = await termService.createDeadlineExtension(
                    selectedTerm.code,
                    data
                );
            } else {
                updatedTerm = await termService.editDeadlineExtension(
                    selectedTerm.code,
                    data
                );
            }

            if (onTermUpdated) onTermUpdated(updatedTerm);
            resetExtForm();
            setExtSuccess('Extension saved.');
        } catch (err) {
            setExtError(
                err.response?.data?.message ||
                    'Failed to save extension. Please try again.'
            );
        } finally {
            setExtSaving(false);
        }
    };

    const startEditExtension = (ext) => {
        setEditingEmail(ext.facultyEmail);
        setExtForm({
            facultyEmail: ext.facultyEmail,
            applicationCloseDate: new Date(ext.applicationCloseDate)
                .toISOString()
                .split('T')[0],
        });
        setExtError(null);
        setExtSuccess(null);
    };

    // Filled when the dialog opens, from the term as it stands then
    useEffect(() => {
        if (show) populate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    return (
        <Modal
            aria-labelledby="pth-modal-title-1"
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title id="pth-modal-title-1">
                    {programLabel} Deadlines, {termDisplayName(selectedTerm)}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form onSubmit={handleSaveDeadlines}>
                    {deadlineError && (
                        <Alert variant="danger" className="mb-3">
                            {deadlineError}
                        </Alert>
                    )}
                    <p className="text-muted small mb-3">
                        Set when the {programName} program takes applications,
                        reviews them, and releases decisions for{' '}
                        {termDisplayName(selectedTerm)}. These dates are
                        independent of other programs.
                    </p>
                    <Form.Group
                        className="mb-3"
                        controlId="pth-application-open-date"
                    >
                        <Form.Label>Applications Open</Form.Label>
                        <Form.Control
                            type="date"
                            value={windowForm.applicationOpenDate}
                            aria-describedby="pth-application-dates-help"
                            onChange={(e) =>
                                setWindowForm({
                                    ...windowForm,
                                    applicationOpenDate: e.target.value,
                                })
                            }
                        />
                    </Form.Group>
                    <Form.Group
                        className="mb-3"
                        controlId="pth-application-close-date"
                    >
                        <Form.Label>Applications Close</Form.Label>
                        <Form.Control
                            type="date"
                            value={windowForm.applicationCloseDate}
                            aria-describedby="pth-application-dates-help"
                            onChange={(e) =>
                                setWindowForm({
                                    ...windowForm,
                                    applicationCloseDate: e.target.value,
                                })
                            }
                        />
                        <Form.Text
                            id="pth-application-dates-help"
                            className="text-muted"
                        >
                            Faculty can apply for {programLabel} support through
                            the end of the close day.{' '}
                            {selectedTerm?.applicationOpenDate ||
                            selectedTerm?.applicationCloseDate
                                ? `Leave a date blank to use the term's: ${formatDate(selectedTerm?.applicationOpenDate)} to ${formatDate(selectedTerm?.applicationCloseDate)}.`
                                : 'Leave a date blank to use the term dates once they are set.'}
                        </Form.Text>
                    </Form.Group>
                    <Form.Group
                        className="mb-3"
                        controlId="pth-review-start-date"
                    >
                        <Form.Label>Review Start Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={windowForm.reviewStart}
                            onChange={(e) =>
                                setWindowForm({
                                    ...windowForm,
                                    reviewStart: e.target.value,
                                })
                            }
                        />
                        <Form.Text className="text-muted">
                            When your program begins reviewing applications
                        </Form.Text>
                    </Form.Group>
                    <Form.Group
                        className="mb-3"
                        controlId="pth-review-end-date"
                    >
                        <Form.Label>Review End Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={windowForm.reviewEnd}
                            onChange={(e) =>
                                setWindowForm({
                                    ...windowForm,
                                    reviewEnd: e.target.value,
                                })
                            }
                        />
                        <Form.Text className="text-muted">
                            Deadline for completing all application reviews
                        </Form.Text>
                    </Form.Group>
                    <Form.Group
                        className="mb-3"
                        controlId="pth-decisions-released"
                    >
                        <Form.Label>Decisions Released</Form.Label>
                        <Form.Control
                            type="date"
                            value={windowForm.decisionsReleased}
                            aria-describedby={
                                clearingRelease
                                    ? 'pth-decisions-help pth-decisions-clear-warning'
                                    : 'pth-decisions-help'
                            }
                            onChange={(e) =>
                                setWindowForm({
                                    ...windowForm,
                                    decisionsReleased: e.target.value,
                                })
                            }
                        />
                        <Form.Text
                            id="pth-decisions-help"
                            className="text-muted"
                        >
                            {releaseSentence(
                                formDateToStored(windowForm.decisionsReleased)
                            )}
                        </Form.Text>
                        {clearingRelease && (
                            <Alert
                                id="pth-decisions-clear-warning"
                                variant="warning"
                                className="mt-2 mb-0"
                            >
                                Saving with no date sends every held decision
                                now.
                            </Alert>
                        )}
                    </Form.Group>
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Deadlines'}
                    </Button>
                </Form>

                <hr />
                <h6>Application extensions</h6>
                <p className="text-muted small">
                    Extend a deadline for a specific faculty.
                </p>

                {extError && (
                    <Alert variant="danger" className="mb-3">
                        {extError}
                    </Alert>
                )}
                {extSuccess && (
                    <Alert variant="success" className="mb-3">
                        {extSuccess}
                    </Alert>
                )}

                <Form onSubmit={handleSaveExtension}>
                    <Form.Group className="mb-3" controlId="pth-faculty-email">
                        <Form.Label>Faculty Email</Form.Label>
                        <Form.Control
                            type="email"
                            value={extForm.facultyEmail}
                            onChange={(e) =>
                                setExtForm({
                                    ...extForm,
                                    facultyEmail: e.target.value,
                                })
                            }
                            required
                            disabled={editingEmail !== null}
                        />
                    </Form.Group>

                    <Form.Group
                        className="mb-3"
                        controlId="pth-extended-application-deadline"
                    >
                        <Form.Label>Extended application deadline</Form.Label>
                        <Form.Control
                            type="date"
                            value={extForm.applicationCloseDate}
                            onChange={(e) =>
                                setExtForm({
                                    ...extForm,
                                    applicationCloseDate: e.target.value,
                                })
                            }
                            required
                        />
                    </Form.Group>

                    <Button type="submit" disabled={extSaving}>
                        {extSaving
                            ? 'Saving...'
                            : editingEmail
                              ? 'Update deadline'
                              : 'Add extension'}
                    </Button>
                </Form>

                {selectedTerm?.deadlineExtensions
                    ?.filter((ext) => ext.programType === programType)
                    .map((ext) => (
                        <div
                            key={ext.facultyEmail}
                            className="d-flex justify-content-between align-items-center border-top py-2"
                        >
                            <span className="small">
                                {ext.facultyEmail} -{' '}
                                {formatDate(ext.applicationCloseDate)}
                            </span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => startEditExtension(ext)}
                            >
                                Edit
                            </Button>
                        </div>
                    ))}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    type="button"
                    variant="outline-primary"
                    onClick={() => {
                        resetExtForm();
                        onHide();
                    }}
                >
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ProgramDeadlinesDialog;
